import { useState } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, BarChart3 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.user, response.data.token);
      toast.success(isLogin ? "Login realizado!" : "Conta criada com sucesso!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar requisição");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - Form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-chivo font-black text-xl tracking-tight">Monitoramento de Ocupação</span>
            </div>
            <p className="text-sm text-zinc-500 font-public">
              Smart Break
            </p>
          </div>

          {/* Title */}
          <div className="mb-8">
            <h1 className="font-chivo font-bold text-3xl tracking-tight mb-2">
              {isLogin ? "Entrar" : "Criar conta"}
            </h1>
            <p className="text-zinc-500 text-sm">
              {isLogin 
                ? "Entre com suas credenciais para acessar o painel" 
                : "Crie sua conta para começar a analisar"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium mb-2 block">
                  Nome
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  placeholder="Seu nome"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-11 rounded-none border-zinc-200 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="h-11 rounded-none border-zinc-200 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium mb-2 block">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-11 rounded-none border-zinc-200 focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-medium"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent animate-spin rounded-full" />
                  Processando...
                </span>
              ) : (
                isLogin ? "Entrar" : "Criar conta"
              )}
            </Button>
          </form>

          {/* Toggle */}
          <div className="mt-8 text-center">
            <p className="text-sm text-zinc-500">
              {isLogin ? "Não tem uma conta?" : "Já tem uma conta?"}
              <button
                type="button"
                data-testid="toggle-auth-mode"
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 text-zinc-900 font-medium hover:underline"
              >
                {isLogin ? "Criar conta" : "Entrar"}
              </button>
            </p>
          </div>

          {/* Footer - Powered by CrowdSA */}
          <div className="mt-12 pt-8 border-t border-zinc-100">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-400">Powered by</span>
                <a href="https://www.crowdsa.com.br" target="_blank" rel="noopener noreferrer">
                  <img 
                    src="/crowdsa-logo.png" 
                    alt="CrowdSA" 
                    className="h-5 w-auto hover:opacity-80 transition-opacity cursor-pointer"
                  />
                </a>
              </div>
              <p className="text-xs text-zinc-400">
                All rights reserved 2026 CrowdSA.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right - Image */}
      <div 
        className="hidden lg:block bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1760776140488-32fcfab4066a?crop=entropy&cs=srgb&fm=jpg&q=85')`
        }}
      >
        <div className="h-full bg-zinc-900/30 flex items-end p-12">
          <div className="text-white">
            <h2 className="font-chivo font-bold text-3xl mb-3">
              Otimize suas gôndolas
            </h2>
            <p className="text-white/80 max-w-md">
              Análise automatizada de ocupação com inteligência artificial para 
              maximizar a eficiência do seu varejo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
